/**
 * The Safe Count submission form (docs/phases/safe-count.md, step 7).
 *
 * One shell, three field-set variants: the location chosen decides which
 * numeric fields appear (`SAFE_COUNT_FIELDS`), in the order ClickUp showed
 * them. Fields mirror `SafeCountSubmissionIn` exactly - there is no Total
 * input, because the backend computes `total` (Decision 1) and this form does
 * no money arithmetic, not even for display; and there is no `submitted_by`,
 * which is bound server-side to the signed-in principal.
 *
 * Photos follow Cash Drop's upload-first flow through the shared
 * `AttachmentSlots` machinery, on Safe Count's own upload endpoint. They are
 * optional here (0-3), where Cash Drop requires at least one.
 */
import { useState, type FormEvent } from "react";

import {
  LOCATIONS,
  SAFE_COUNT_FIELDS,
  SAFE_COUNT_FIELD_LABELS,
  submitSafeCount,
  uploadSafeCountAttachment,
  type Location,
  type SafeCountFieldKey,
  type SafeCountFields,
  type SafeCountRecord,
} from "../api/public";
import type { TokenGetter } from "../auth/session";
import { AttachmentSlots, errorMessage, useAttachmentSlots } from "../components/AttachmentSlots";
import { SessionGate } from "../components/SessionGate";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Same rules as Cash Drop: fold a comma decimal separator to a period (iOS's
 * decimal keypad can render one depending on device region) and add a
 * leading zero to a bare decimal (`.01`, exactly what that keypad produces
 * with no "0" key press before it) - both normalized before the validity
 * check and the value sent. The backend's `Decimal` field would accept a raw
 * `.01` fine, so this is purely for `isValidMoney` and consistency. */
function normalizeMoney(value: string): string {
  const folded = value.trim().replace(",", ".");
  return folded.startsWith(".") ? `0${folded}` : folded;
}

/** Plain digits-and-dot, non-negative, exactly what the manager typed (after
 * `normalizeMoney`) is what gets sent (`Decimal, Field(ge=0)` on the backend). */
function isValidMoney(value: string): boolean {
  return /^\d+(\.\d+)?$/.test(normalizeMoney(value));
}

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success"; record: SafeCountRecord }
  | { status: "error"; message: string };

export function SubmitSafeCount() {
  return <SessionGate>{(getToken) => <SubmitSafeCountForm getToken={getToken} />}</SessionGate>;
}

function SubmitSafeCountForm({ getToken }: { getToken: TokenGetter }) {
  const [location, setLocation] = useState<Location | "">("");
  const [asOf, setAsOf] = useState(todayIso());
  const [values, setValues] = useState<SafeCountFields>({});
  const uploads = useAttachmentSlots(uploadSafeCountAttachment, getToken);
  const [submission, setSubmission] = useState<SubmitState>({ status: "idle" });

  const fieldKeys: readonly SafeCountFieldKey[] = location === "" ? [] : SAFE_COUNT_FIELDS[location];

  // Submit is never hard-disabled on field validity - same reasoning and
  // incident as Cash Drop's `SubmitCashDrop.tsx`: a disabled button with no
  // `:disabled` styling is indistinguishable from an enabled one. This runs
  // on click and surfaces every problem inline, the same
  // `submission.status === "error"` panel a server rejection uses.
  function validationErrors(): string[] {
    const errors: string[] = [];
    if (location === "") errors.push("Choose a location.");
    if (asOf.trim() === "") errors.push("Enter the safe count date.");
    for (const key of fieldKeys) {
      if (!isValidMoney(values[key] ?? "")) {
        errors.push(`Enter a valid ${SAFE_COUNT_FIELD_LABELS[key]} amount.`);
      }
    }
    if (uploads.anyUploading) errors.push("Wait for the photo upload to finish.");
    return errors;
  }

  function handleLocationChange(next: Location) {
    setLocation(next);
    // Keep what was typed for fields the new location also counts (Coins is
    // on every set); drop the ones it does not, so nothing off-schema is sent.
    setValues((prev) => {
      const kept: SafeCountFields = {};
      for (const key of SAFE_COUNT_FIELDS[next]) {
        const value = prev[key];
        if (value !== undefined) kept[key] = value;
      }
      return kept;
    });
  }

  function resetForm() {
    setLocation("");
    setAsOf(todayIso());
    setValues({});
    uploads.reset();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submission.status === "submitting") return; // guards against a double tap
    const errors = validationErrors();
    // `location === ""` is folded into this guard (already implied whenever
    // `errors` is non-empty), the same control-flow trick `SubmitCashDrop`
    // relies on, so TypeScript narrows `location` to `Location` below.
    if (errors.length > 0 || location === "") {
      setSubmission({ status: "error", message: errors.join(" ") });
      return;
    }
    const fields: SafeCountFields = {};
    for (const key of fieldKeys) fields[key] = normalizeMoney(values[key] ?? "");
    setSubmission({ status: "submitting" });
    try {
      const token = await getToken();
      const record = await submitSafeCount(
        { location, safe_count_as_of: asOf, fields, attachment_ids: uploads.attachmentIds },
        token,
      );
      setSubmission({ status: "success", record });
      resetForm();
    } catch (error) {
      setSubmission({ status: "error", message: errorMessage(error) });
    }
  }

  return (
    <>
      {submission.status === "success" && (
        <section className="card success" data-testid="submit-success">
          <h2>Submitted</h2>
          <dl className="facts">
            <dt>Location</dt>
            <dd>{submission.record.location}</dd>
            <dt>Safe count as of</dt>
            <dd>{submission.record.safe_count_as_of}</dd>
            {SAFE_COUNT_FIELDS[submission.record.location].map((key) => (
              <FactRow key={key} label={SAFE_COUNT_FIELD_LABELS[key]} value={submission.record.fields[key]} />
            ))}
            <dt>Total</dt>
            <dd>{submission.record.total}</dd>
          </dl>
        </section>
      )}

      {/* noValidate: see SubmitCashDrop.tsx - our own `validationErrors()` is
          the one source of truth, not the browser's native required/pattern
          validation, which would otherwise silently swallow the submit event
          for an empty required field before our inline message ever runs. */}
      <form className="card" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <h2>Safe Count</h2>

        {submission.status === "error" && (
          <p className="field-error" role="alert" data-testid="submit-error">
            {submission.message}
          </p>
        )}

        <label className="field">
          Location
          <select
            value={location}
            onChange={(e) => handleLocationChange(e.target.value as Location)}
            required
          >
            <option value="" disabled>
              Choose a location
            </option>
            {LOCATIONS.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          Safe count as of
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} required />
        </label>

        {location === "" ? (
          <p className="muted" data-testid="count-fields-hint">
            Choose a location to see its count fields.
          </p>
        ) : (
          <fieldset className="count-fields" data-testid="count-fields">
            <legend className="muted">Count</legend>
            {fieldKeys.map((key) => (
              <label className="field" key={key}>
                {SAFE_COUNT_FIELD_LABELS[key]}
                <input
                  type="text"
                  name={key}
                  inputMode="decimal"
                  placeholder="0.00"
                  value={values[key] ?? ""}
                  onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
                  required
                />
              </label>
            ))}
            <p className="muted">Total is computed when you submit.</p>
          </fieldset>
        )}

        <AttachmentSlots
          legend="Photos (optional, up to 3)"
          slots={uploads.slots}
          onChange={uploads.handleSlotInputChange}
          onClear={uploads.clearSlot}
        />

        <button type="submit" disabled={submission.status === "submitting"}>
          {submission.status === "submitting" ? "Submitting…" : "Submit"}
        </button>
      </form>
    </>
  );
}

function FactRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value ?? "—"}</dd>
    </>
  );
}
